/**
 * El color de un participante.
 *
 * **Una paleta cerrada y no un selector libre.** Un `<input type="color">`
 * deja elegir amarillo sobre blanco o gris sobre gris, y el color deja de
 * servir justo para lo que se puso: reconocer de un vistazo. Estos ocho están
 * elegidos para leerse sobre fondo claro y oscuro, y para distinguirse entre
 * ellos también por tono y no sólo por brillo.
 *
 * Se guarda el NOMBRE, no el código: así el mismo participante se ve bien en
 * los dos temas, porque cada nombre resuelve a un valor distinto en cada uno.
 * Guardar `#1f7a4d` obligaría a que ese verde funcionara en ambos, y no
 * funciona.
 */
export const COLORES = [
  { id: 'verde', nombre: 'verde', claro: '#1f7a4d', oscuro: '#5fbf8c' },
  { id: 'celeste', nombre: 'celeste', claro: '#2b6ea8', oscuro: '#6fa8d8' },
  { id: 'rojo', nombre: 'rojo', claro: '#b5342a', oscuro: '#e2796d' },
  { id: 'naranja', nombre: 'naranja', claro: '#a8590f', oscuro: '#e0a05a' },
  { id: 'violeta', nombre: 'violeta', claro: '#6b3fa0', oscuro: '#b191e0' },
  { id: 'turquesa', nombre: 'turquesa', claro: '#0f7c85', oscuro: '#5cc6cf' },
  { id: 'rosa', nombre: 'rosa', claro: '#a83262', oscuro: '#e08bae' },
  { id: 'gris', nombre: 'gris', claro: '#4c5a57', oscuro: '#a6b3af' },
] as const;

export type IdColor = (typeof COLORES)[number]['id'];

/** El valor CSS del color, o `null` si el participante no tiene uno. */
export function colorDe(id: string | null | undefined, oscuro = false): string | null {
  const c = COLORES.find((x) => x.id === id);
  return c ? (oscuro ? c.oscuro : c.claro) : null;
}

/**
 * Elegir el color, con la paleta a la vista.
 *
 * Los ocho se muestran siempre en vez de esconderlos en un desplegable: son
 * pocos, y elegir un color comparándolo con los demás es la única forma de
 * elegirlo bien.
 */
export function ColorParticipante({
  valor,
  onChange,
}: {
  valor: string | null;
  onChange: (c: string | null) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="color-participante">
      {COLORES.map((c) => {
        const activo = valor === c.id;
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => onChange(activo ? null : c.id)}
            aria-pressed={activo}
            aria-label={c.nombre}
            title={c.nombre}
            // El elegido se marca con un anillo y no sólo con el relleno: dos
            // colores contiguos de brillo parecido no se distinguen por el
            // relleno solo, y quien no ve bien el color no ve nada.
            className={`size-8 rounded-full border-2 ${
              activo ? 'border-slate-900 ring-2 ring-offset-1' : 'border-transparent'
            }`}
            style={{ backgroundColor: c.claro }}
            data-testid={`color-${c.id}`}
          />
        );
      })}
      {valor && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="min-h-11 px-2 text-xs underline opacity-60"
          data-testid="color-quitar"
        >
          sin color
        </button>
      )}
    </div>
  );
}
